import React from 'react';
import { Link } from 'react-router-dom';
import { Handshake, Building2, Wine, Sparkles } from 'lucide-react';

// Confirmed sponsors. Drop a PNG/SVG into /public/sponsors/<slug>.<ext> to upgrade
// any text-only card to a real logo card.
const SPONSORS = [
  { slug: 'spoton', name: 'SpotOn', logo: '/sponsors/spoton.png' },
  { slug: 'chun-cordasco', name: 'Chun, Cordasco & Associates', logo: '/sponsors/chun-cordasco.png', darkBg: true },
  { slug: 'campari', name: 'Campari USA', logo: '/sponsors/campari.svg' },
  { slug: 'brown-forman', name: 'Brown-Forman', logo: '/sponsors/brown-forman.svg', darkBg: true },
  { slug: 'tres-tequila', name: '3 Tres Tequila', logo: '/sponsors/tres-tequila.png' },
  { slug: 'aloha-alkaline', name: 'Aloha Alkaline', logo: '/sponsors/aloha-alkaline.png', darkBg: true },
  { slug: 'pernod-ricard', name: 'Pernod Ricard', logo: '/sponsors/pernod-ricard.png' },
  { slug: 'proximo', name: 'Proximo', logo: '/sponsors/proximo.svg', darkBg: true },
  { slug: 'tequila-ocho', name: 'Tequila Ocho', logo: '/sponsors/tequila-ocho.svg', darkBg: true },
  { slug: 'g4-tequila', name: 'G4 Tequila', logo: '/sponsors/g4-tequila.png', darkBg: true },
  { slug: 'island-structural', name: 'Island Structural Contracting', logo: '/sponsors/island-structural.png', darkBg: true },
  { slug: 'island-beverage', name: 'Island Beverage', logo: '/sponsors/island-beverage.png', darkBg: true },
  { slug: 'twilight-audio', name: 'Twilight Audio', logo: '/sponsors/twilight-audio.jpg' },
  { slug: 'centered', name: 'Centered', logo: '/sponsors/centered.png' },
  { slug: 'inu', name: 'INU', logo: '/sponsors/inu.png' },
  { slug: 'miko-francom', name: 'Miko Francom', logo: '/sponsors/miko-francom.png' },
  { slug: 'haley-von-katz', name: 'Haley von Katz', logo: '/sponsors/haley-von-katz.png', darkBg: true },
  { slug: 'rads-cosmos', name: "Rad's Cosmos", logo: '/sponsors/rads-cosmos.png', darkBg: true },
  { slug: 'la-marea', name: 'Taqueria La Marea', logo: '/sponsors/la-marea.png' },
  { slug: 'shaka-mex', name: 'Shaka Mex', logo: '/sponsors/shaka-mex.png' },
  { slug: 'la-pina-cantina', name: 'La Piña Cantina', logo: '/sponsors/la-pina-cantina.png', darkBg: true },
  { slug: '13-eleven-events', name: '13 Eleven Events', logo: '/sponsors/13-eleven-events.jpg' },
  { slug: 'pitch-sports-bar', name: 'Pitch Sports Bar Kakaʻako', logo: '/sponsors/pitch-sports-bar.png', darkBg: true },
];

const SponsorCard = ({ sponsor }) => {
  const [logoOk, setLogoOk] = React.useState(Boolean(sponsor.logo));
  const hasLogo = logoOk && sponsor.logo;
  const darkLogoCard = hasLogo && sponsor.darkBg;
  const lightLogoCard = hasLogo && !sponsor.darkBg;

  let cardClasses;
  if (lightLogoCard) {
    cardClasses = 'bg-white hover:bg-white border border-white/30 hover:border-pink-500/40 hover:shadow-2xl hover:shadow-pink-500/20';
  } else if (darkLogoCard) {
    cardClasses = 'bg-black hover:bg-black border border-white/20 hover:border-pink-500/40 hover:shadow-2xl hover:shadow-pink-500/20';
  } else {
    cardClasses = 'bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-pink-500/40';
  }

  return (
    <div
      data-testid={`sponsor-card-${sponsor.slug}`}
      className={`group aspect-[3/2] flex items-center justify-center rounded-2xl p-4 sm:p-5 transition-all duration-300 hover:-translate-y-1 overflow-hidden ${cardClasses}`}
    >
      {hasLogo ? (
        <img
          src={sponsor.logo}
          alt={`${sponsor.name} logo`}
          onError={() => setLogoOk(false)}
          className="max-h-full max-w-full object-contain opacity-90 group-hover:opacity-100 transition-opacity duration-300"
          loading="lazy"
        />
      ) : (
        <span className="text-center text-white/85 group-hover:text-white text-sm sm:text-base font-bold tracking-tight leading-tight transition-colors duration-300">
          {sponsor.name}
        </span>
      )}
    </div>
  );
};

const SponsorsComingSoonSection = () => {
  return (
    <section
      id="sponsors"
      className="py-20 sm:py-24 bg-gradient-to-b from-black via-gray-950 to-black relative overflow-hidden"
      data-testid="sponsors-section"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] bg-gradient-radial from-pink-500/10 via-orange-500/5 to-transparent rounded-full blur-3xl" />

      <div className="relative z-10 max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/30 mb-4">
            <Handshake className="w-4 h-4 text-pink-400" />
            <span className="text-pink-300 text-sm font-medium tracking-wider uppercase">Our Sponsors</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-orange-400 to-yellow-400 mb-4">
            Powered By Our Partners
          </h2>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
            Mahalo to the brands, agencies and agave producers helping us bring Hawaii&apos;s premier tequila festival to life on June 20, 2026.
          </p>
        </div>

        {/* Sponsor grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-12" data-testid="sponsors-grid">
          {SPONSORS.map((s) => (
            <SponsorCard key={s.slug} sponsor={s} />
          ))}
        </div>

        {/* Partner-type pills */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10">
          {[
            { icon: Wine, label: 'Beverage Brand Partners' },
            { icon: Building2, label: 'Business Sponsors' },
            { icon: Handshake, label: 'Hospitality Partners' },
            { icon: Sparkles, label: 'Media Partners' }
          ].map((p) => {
            const Icon = p.icon;
            return (
              <span
                key={p.label}
                className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white/75 text-xs sm:text-sm px-3 py-1.5 rounded-full"
              >
                <Icon className="w-3.5 h-3.5 text-pink-400" />
                {p.label}
              </span>
            );
          })}
        </div>

        {/* CTAs to apply */}
        <div className="bg-gradient-to-br from-pink-500/15 via-orange-500/10 to-yellow-500/5 border border-pink-500/30 rounded-3xl p-6 sm:p-8 text-center">
          <h3 className="text-xl sm:text-2xl font-black text-white mb-2">Want your brand alongside these partners?</h3>
          <p className="text-white/75 text-sm sm:text-base mb-5 max-w-2xl mx-auto">
            We&apos;re still adding founding partners across hospitality, media, and beverage. Sponsors get logo placement, dedicated activation space, and full-festival visibility.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/vendor-sponsor"
              data-testid="sponsors-cta-business"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-bold px-6 py-3 rounded-full transition-all duration-300 hover:scale-105"
            >
              <Building2 className="w-4 h-4" />
              Apply to Sponsor
            </Link>
            <Link
              to="/vendor-sponsor"
              data-testid="sponsors-cta-beverage"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 text-white font-bold px-6 py-3 rounded-full transition-all duration-300 hover:scale-105"
            >
              <Wine className="w-4 h-4" />
              Apply as Beverage Exhibitor
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SponsorsComingSoonSection;
